import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { HttpError, HttpService } from 'src/toolkit';
import { TTableSort, TableSort } from 'src/components/table/thead/col';

import { useAppSelector } from '../../hooks';
import { SystemSettingValuesSortCol, TSystemSettingList, TSystemSettingValueListResponse, TSystemSettingValueQuery, TSystemSettingValuesSortCol, adaptSystemSettingValueFromListResponse, adaptSystemSettingValuesQueryToRequest } from '../../types';
import { BACKEND_ADMIN_SYSTEM_SETTINGS_SHIFTS } from '../../backend-paths';


export const systemSettingShiftGetApi = createAsyncThunk(
	BACKEND_ADMIN_SYSTEM_SETTINGS_SHIFTS,
	(payload: TSystemSettingValueQuery | undefined, thunkApi) => {
		const params = adaptSystemSettingValuesQueryToRequest(payload);

		return HttpService.get<TSystemSettingValueListResponse>(BACKEND_ADMIN_SYSTEM_SETTINGS_SHIFTS, { params }).then(result => {
			return adaptSystemSettingValueFromListResponse(result.data);
		}).catch((error: HttpError) => {
			return thunkApi.rejectWithValue({ ...error });
		});
	},
);

type TInitialState = {
	isLoading: boolean;
	list: TSystemSettingList;
	query: TSystemSettingValueQuery;
	error?: HttpError;
};

const initialState: TInitialState = {
	isLoading: false,
	list: {
		items: [],
		count: 0,
	},
	query: {
		limit: 10,
		offset: 0,
		search: undefined,
		sort: TableSort.ASC,
		sortCol: SystemSettingValuesSortCol.CREATED_AT,
	},
	error: undefined,
};

export const systemSettingShiftSlice = createSlice({
	name: 'systemSettingShiftSlice',
	initialState,
	reducers: {
		setSystemSettingShiftQueryLimit(state, action: PayloadAction<number>) {
			state.query.limit = action.payload;
		},

		setSystemSettingShiftQueryOffset(state, action: PayloadAction<number>) {
			state.query.offset = action.payload;
		},

		setSystemSettingShiftQuerySearch(state, action: PayloadAction<string>) {
			const search = action.payload || undefined;
			const isSearch = search && search.length > 0;

			if (isSearch && state.query.offset !== 0) {
				state.query.offset = 0;
				return;
			}

			state.query.search = search;
		},

		setSystemSettingShiftQuerySort(state, action: PayloadAction<TTableSort>) {
			state.query.sort = action.payload;
		},

		setSystemSettingShiftQuerySortCol(state, action: PayloadAction<TSystemSettingValuesSortCol>) {
			state.query.sortCol = action.payload;
		},

		clearSystemSettingShift(state) {
			return { ...initialState, list: state.list, query: state.query };
		},
	},

	extraReducers: {
		[systemSettingShiftGetApi.pending.type]: (state) => {
			state.isLoading = true;
			state.error = undefined;
		},

		[systemSettingShiftGetApi.fulfilled.type]: (state, action: PayloadAction<TSystemSettingList>) => {
			state.isLoading = false;
			state.list = action.payload;
			state.error = undefined;
		},

		[systemSettingShiftGetApi.rejected.type]: (state, action: PayloadAction<HttpError>) => {
			state.isLoading = false;
			state.error = action.payload;
		},
	},
});

export const useSystemSettingShiftSelector = () => useAppSelector(state => state[systemSettingShiftSlice.name]);

export const { clearSystemSettingShift, setSystemSettingShiftQueryLimit, setSystemSettingShiftQueryOffset, setSystemSettingShiftQuerySort, setSystemSettingShiftQuerySortCol, setSystemSettingShiftQuerySearch } = systemSettingShiftSlice.actions;
